/**
 * @file: useTaskBookmarks.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { TaskBookmark, TaskBookmarkSavePayload } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect } from 'react';
import { Subject } from 'rxjs';

const kDefaultLimit = 21;

export const taskBookmarkAdded$ = new Subject<TaskBookmark>();
export const taskBookmarkRemoved$ = new Subject<string>();

export const useAddTaskBookmark = () => {
  const { userService } = useServiceFactory();
  return useCallback(
    async (payload: TaskBookmarkSavePayload) => {
      const bookmark = await userService.addTaskBookmark(payload);
      taskBookmarkAdded$.next(bookmark);
      return bookmark;
    },
    [userService],
  );
};

export const useRemoveTaskBookmark = () => {
  const { userService } = useServiceFactory();
  return useCallback(
    async (taskNo: string) => {
      await userService.deleteTaskBookmarkByTaskNo(taskNo);
      taskBookmarkRemoved$.next(taskNo);
    },
    [userService],
  );
};

export const useTaskBookmarks = (options?: { limit?: number }) => {
  const { userService } = useServiceFactory();
  const limit = options?.limit ?? kDefaultLimit;

  const query = useCallback(
    async (offset: number) => {
      __DEV__ && console.log('loading task bookmarks at ', offset);
      return await userService.getTaskBookmarks(offset, limit);
    },
    [limit, userService],
  );

  const list = useInfiniteList({ limit, query });

  const refresh = usePersistFn(() => {
    void list.fetch();
  });

  const remove = usePersistFn((taskNo: string) => {
    const index = list.data.findIndex(x => x.taskNo === taskNo);
    if (index >= 0) {
      void list.invalidate(index);
    }
  });

  useEffect(() => {
    const added = taskBookmarkAdded$.subscribe(() => {
      refresh();
    });

    const removed = taskBookmarkRemoved$.subscribe(taskNo => {
      remove(taskNo);
    });

    return () => {
      added.unsubscribe();
      removed.unsubscribe();
    };
  }, [refresh, remove]);

  const { fetch } = list;

  useEffect(() => {
    __DEV__ && console.log('begin loading task bookmarks... ');
    void fetch();
  }, [fetch]);

  return { list };
};
